import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CURRENCIES, TIME_ZONES, initials, type NotificationPrefs, type TeamMemberDto, type WorkspaceSettingsDto } from '@coachrex/shared';
import { useSaveSettings, useSettings, useSyncTeam, useTeam, useUpdateMember } from '../lib/api';
import { relativeStamp } from '../lib/format';
import { ErrorBox, PageHeader, Pills, Skeleton } from '../components/ui';
import { useToast } from '../components/Toast';
import './settings.css';

type Tab = 'team' | 'workspace' | 'notifications';

/** Handoff screen 12. */
export function Settings() {
  const [params, setParams] = useSearchParams();
  const tab = (['team', 'workspace', 'notifications'].includes(params.get('tab') ?? '') ? params.get('tab') : 'team') as Tab;
  const settings = useSettings();
  return (
    <div className="page">
      <PageHeader title="Team & workspace" subtitle={settings.data ? `${settings.data.name} workspace` : ' '} />
      <Pills
        options={[
          { value: 'team', label: 'Team' },
          { value: 'workspace', label: 'Workspace' },
          { value: 'notifications', label: 'Notifications' },
        ]}
        value={tab}
        onChange={(t) => setParams(t === 'team' ? {} : { tab: t }, { replace: true })}
      />
      {tab === 'team' ? <Team /> : settings.error ? <ErrorBox error={settings.error} /> : !settings.data ? <Skeleton height={300} /> : <WorkspaceForm initial={settings.data} part={tab} />}
    </div>
  );
}

// ---------- Team ----------

type Filter = 'active' | 'inactive' | 'all';

function Team() {
  const { data, error, isLoading } = useTeam();
  const sync = useSyncTeam();
  const update = useUpdateMember();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('active');
  const members = data?.members ?? [];
  const shown = members.filter((m) => filter === 'all' || (filter === 'active' ? m.active : !m.active));
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const change = (m: TeamMemberDto, patch: { role?: 'manager' | 'rep'; active?: boolean }, text: string) => {
    const before = { role: patch.role !== undefined ? m.role : undefined, active: patch.active !== undefined ? m.active : undefined };
    update.mutate(
      { id: m.id, ...patch },
      { onSuccess: () => toast({ text, undo: () => update.mutateAsync({ id: m.id, ...before }) }) },
    );
  };

  return (
    <>
      <div className="team-tools">
        <Pills
          options={[
            { value: 'active', label: 'Active', count: members.filter((m) => m.active).length },
            { value: 'inactive', label: 'Inactive', count: members.filter((m) => !m.active).length },
            { value: 'all', label: 'All', count: members.length },
          ]}
          value={filter}
          onChange={setFilter}
        />
        <span className="team-tools__sync">
          {data && (
            <span className="card-meta">
              {members.length} users imported from HighLevel{data.syncedAt ? ` · synced ${relativeStamp(data.syncedAt, tz).toLowerCase()}` : ''}
            </span>
          )}
          <button className="btn btn--secondary btn--sm" disabled={sync.isPending} onClick={() => sync.mutate()}>
            {sync.isPending ? 'Syncing…' : 'Sync users now'}
          </button>
        </span>
      </div>
      {(sync.error || update.error) && <ErrorBox error={sync.error ?? update.error} />}
      <section className="card card--list">
        <div className="list-head team-grid">
          <span>HighLevel user</span>
          <span>HL role</span>
          <span>Coach Lex role</span>
          <span>Status</span>
          <span style={{ textAlign: 'right' }}>Last activity</span>
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={44} />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <div className="list-empty">{filter === 'inactive' ? 'Nobody is inactive.' : 'No users yet. Sync users to import them from HighLevel.'}</div>
        ) : (
          shown.map((m) => {
            const stale = !m.lastActivityAt || Date.now() - Date.parse(m.lastActivityAt) > 30 * 86_400_000;
            return (
              <div key={m.id} className="list-row team-grid" style={{ opacity: m.active ? 1 : 0.5 }}>
                <span className="team-user">
                  <span className="team-avatar">{initials(m.name).slice(0, 2)}</span>
                  <span className="team-user__text">
                    <span style={{ fontWeight: 600 }}>{m.name}</span>
                    <span className="card-meta">{m.email ?? '—'}</span>
                  </span>
                </span>
                <span>
                  <span className="chan-chip">{m.crmRole === 'admin' ? 'Admin' : 'User'}</span>
                </span>
                <span>
                  <select
                    className={`role-select${m.role === 'manager' ? ' is-manager' : ''}`}
                    value={m.role}
                    aria-label={`Coach Lex role for ${m.name}`}
                    disabled={update.isPending}
                    onChange={(e) => {
                      const role = e.target.value as 'manager' | 'rep';
                      change(m, { role }, `${m.name} is now a ${role === 'manager' ? 'Manager' : 'Rep'}`);
                    }}
                  >
                    <option value="manager">Manager</option>
                    <option value="rep">Rep</option>
                  </select>
                </span>
                <span className="team-status">
                  <button
                    className={`toggle${m.active ? ' is-on' : ''}`}
                    role="switch"
                    aria-checked={m.active}
                    aria-label={`${m.name} active`}
                    disabled={update.isPending}
                    onClick={() => change(m, { active: !m.active }, m.active ? `${m.name} set to inactive. They’re ignored everywhere; history is kept.` : `${m.name} is active again`)}
                  />
                  <span style={{ color: m.active ? 'var(--green-fg)' : 'var(--faint)', fontSize: 13 }}>{m.active ? 'Active' : 'Inactive'}</span>
                </span>
                <span style={{ textAlign: 'right', fontSize: 12.5, color: stale ? 'var(--amber-fg)' : 'var(--faint)' }}>
                  {m.lastActivityAt ? relativeStamp(m.lastActivityAt, tz) : 'No activity'}
                </span>
              </div>
            );
          })
        )}
      </section>
      <span className="footnote-text">
        Everyone comes from HighLevel automatically; there are no invites. Inactive users are ignored on the Leaderboard, Coaching, rep filters, alerts and Ask Lex. Their history is kept.
      </span>
    </>
  );
}

// ---------- Workspace + Notifications ----------

const NOTIFY: { key: keyof NotificationPrefs; title: string; help: string }[] = [
  { key: 'highValueRescue', title: 'High-value rescue alert', help: 'As soon as a High value lead is flagged for rescue.' },
  { key: 'dailySummary', title: 'Daily summary', help: 'Each morning: leads that need a reply today and yesterday’s recoveries.' },
  { key: 'weeklyCoaching', title: 'Weekly coaching report', help: 'Monday: rep scores, pass rates and moments to review per rep.' },
  { key: 'unansweredLead', title: 'Unanswered lead alert', help: 'When any new lead waits past the reply target.' },
];

function WorkspaceForm({ initial, part }: { initial: WorkspaceSettingsDto; part: 'workspace' | 'notifications' }) {
  const save = useSaveSettings();
  const toast = useToast();
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [initial]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const set = <K extends keyof WorkspaceSettingsDto>(k: K, v: WorkspaceSettingsDto[K]) => setDraft({ ...draft, [k]: v });
  const num = (v: string) => (v === '' ? 0 : Math.max(0, Math.round(Number(v)) || 0));
  const valid = draft.name.trim() && draft.replyTargetMin >= 1 && draft.backToOpenHours >= 1;

  const onSave = () => save.mutate({ ...draft, name: draft.name.trim() }, { onSuccess: () => toast({ text: 'Settings saved' }) });

  return (
    <>
      {part === 'workspace' ? (
        <section className="card settings-card">
          <Field label="Workspace name">
            <input className="text-input" value={draft.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="Time zone" help="Used for “today”, daily charts and timestamps.">
            <select className="text-input" value={draft.timeZone} onChange={(e) => set('timeZone', e.target.value)}>
              {!TIME_ZONES.some((t) => t.value === draft.timeZone) && <option value={draft.timeZone}>{draft.timeZone}</option>}
              {TIME_ZONES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Currency">
            <select className="text-input" value={draft.currency} onChange={(e) => set('currency', e.target.value)}>
              {!CURRENCIES.some((c) => c.value === draft.currency) && <option value={draft.currency}>{draft.currency}</option>}
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reply target" help="An inbound call, text or email with no rep response after this long counts as awaiting a reply and can be flagged for rescue.">
            <span className="unit-input">
              <input className="text-input" inputMode="numeric" value={draft.replyTargetMin || ''} onChange={(e) => set('replyTargetMin', num(e.target.value))} />
              minutes
            </span>
          </Field>
          <Field label="Back to Open after" help="A Completed rescue lead goes back to Open if the lead doesn’t reply within this time.">
            <span className="unit-input">
              <input className="text-input" inputMode="numeric" value={draft.backToOpenHours || ''} onChange={(e) => set('backToOpenHours', num(e.target.value))} />
              hours
            </span>
          </Field>
          <Field label="Default lead value" help="Used for pipeline at risk and recovered revenue when a lead has no opportunity value in HighLevel.">
            <span className="unit-input">
              <input className="text-input" inputMode="numeric" value={draft.defaultLeadValue || ''} placeholder="0" onChange={(e) => set('defaultLeadValue', num(e.target.value))} />
              {draft.currency}
            </span>
          </Field>
        </section>
      ) : (
        <section className="card settings-card">
          {NOTIFY.map((n) => (
            <div key={n.key} className="notify-row">
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{n.title}</span>
                <span className="card-meta">{n.help}</span>
              </span>
              <button
                className={`toggle${draft.notifications[n.key] ? ' is-on' : ''}`}
                role="switch"
                aria-checked={draft.notifications[n.key]}
                aria-label={n.title}
                onClick={() => set('notifications', { ...draft.notifications, [n.key]: !draft.notifications[n.key] })}
              />
            </div>
          ))}
          <span className="hint-box">Alerts go out once Slack, email or SMS is connected in Integrations. Your choices here are saved now.</span>
        </section>
      )}
      <div className="save-bar">
        {save.error && <ErrorBox error={save.error} />}
        {dirty && <span className="footnote-text">Unsaved changes.</span>}
        {dirty && (
          <button className="btn btn--secondary" onClick={() => setDraft(initial)}>
            Discard
          </button>
        )}
        <button className="btn btn--accent" disabled={!dirty || !valid || save.isPending} onClick={onSave}>
          {save.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </>
  );
}

function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="settings-field">
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</span>
        {help && <span className="card-meta">{help}</span>}
      </span>
      <span>{children}</span>
    </div>
  );
}
